import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { log } from "./logger";

/**
 * useAudio provides pronounce() for cards.
 * Priority: card.audio_url (user recording) > OpenAI TTS (native voice) > browser SpeechSynthesis (fallback)
 */
export function useAudio(language) {
  const [supported] = useState(typeof window !== "undefined" && "speechSynthesis" in window);
  const [cache] = useState(() => new Map()); // text -> base64 URL

  const speakBrowser = (text) => {
    if (!supported) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      const langMap = {
        Spanish: "es-ES", French: "fr-FR", Japanese: "ja-JP",
        German: "de-DE", Italian: "it-IT", Portuguese: "pt-BR",
        Korean: "ko-KR", Mandarin: "zh-CN", Russian: "ru-RU",
      };
      u.lang = langMap[language] || "en-US";
      u.rate = 0.9;
      window.speechSynthesis.speak(u);
    } catch (err) {
      log.error("Browser TTS failed", err);
    }
  };

  const pronounce = async (card, options = {}) => {
    const source = options.source || "auto"; // auto | browser | ai | recording
    // Card orientation: front = target language (audio target), back = English meaning.
    const text = options.text || card?.front;
    if (!text) return;

    if (source !== "browser" && card?.audio_url) {
      // User-recorded audio takes priority in "auto" and "recording" modes
      try {
        const url = card.audio_url.startsWith("http")
          ? card.audio_url
          : `${process.env.REACT_APP_BACKEND_URL}${card.audio_url}`;
        const audio = new Audio(url);
        await audio.play();
        return;
      } catch (err) {
        log.error("Audio playback failed", err);
      }
    }

    if (source === "browser") {
      speakBrowser(text);
      return;
    }

    // AI TTS via backend (cached in-memory)
    if (source === "ai" || source === "auto") {
      const cacheKey = `${language}::${text}`;
      if (cache.has(cacheKey)) {
        try {
          const audio = new Audio(cache.get(cacheKey));
          await audio.play();
          return;
        } catch (err) {
          log.error("Cached TTS playback failed", err);
        }
      }
      try {
        const { data } = await api.post("/ai/tts", { text, language });
        const url = `data:audio/mp3;base64,${data.audio_base64}`;
        cache.set(cacheKey, url);
        const audio = new Audio(url);
        await audio.play();
        return;
      } catch (err) {
        log.error("AI TTS failed, falling back to browser TTS", err);
        speakBrowser(text);
      }
    }
  };

  return { pronounce, supported };
}
